/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, SelectField, SelectOption, Skeleton } from '@coursera/cds-core';

import { LEARNING_GOAL_OPTIONS } from 'bundles/onboarding-common/constants';
import type { LEARNING_GOAL_OPTIONS_TYPE } from 'bundles/onboarding-common/constants';
import useLearnerObjectLearningGoalData from 'bundles/onboarding-common/hooks/useLearnerObjectLearningGoalData';
import useLearnerObjectUpdateLearningGoal from 'bundles/onboarding-common/hooks/useLearnerObjectUpdateLearningGoal';
import { getLearningGoalOptionName } from 'bundles/onboarding-common/utils';
import SettingRow from 'bundles/personalization-settings/components/SettingRow';

import _t from 'i18n!nls/personalization-settings';

// CDS SelectField's `fullWidth` only widens the outer FormControl, not the
// input box itself, leaving the visible control shrunk to its content width
// instead of filling the row's second column. Force it via CDS's own public
// class hook (see EducationLevelSettingItem for the same workaround).
const styles = {
  select: css`
    .cds-select-field-root {
      width: 100%;
    }
  `,
};

const LABEL_ID = 'learning-goal-label';

export type LearningGoalSettingItemProps = {
  label: string;
};

const LEARNING_GOAL_OPTION_IDS = Object.values(LEARNING_GOAL_OPTIONS);
const LEARNING_GOAL_OPTION_ID_SET = new Set<string>(LEARNING_GOAL_OPTION_IDS);

const isLearningGoalOption = (value: string): value is LEARNING_GOAL_OPTIONS_TYPE =>
  LEARNING_GOAL_OPTION_ID_SET.has(value);

// No saving/saved/error state yet -- that lands with LO-4760; a failed save
// here just leaves the selection as-is.
const LearningGoalSettingItem = ({ label }: LearningGoalSettingItemProps) => {
  const { value: savedValue, loading } = useLearnerObjectLearningGoalData({});
  const { updateLearningGoal } = useLearnerObjectUpdateLearningGoal();
  const optionNames = getLearningGoalOptionName();

  // Overrides the saved value once the learner picks something this session --
  // the query never refetches after a save, so falling back to it directly
  // would never visibly reflect a new selection.
  const [override, setOverride] = React.useState<LEARNING_GOAL_OPTIONS_TYPE | null>(null);
  const selectedValue = override ?? savedValue;

  const handleChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const { value: nextValue } = event.target;
    if (typeof nextValue === 'string' && isLearningGoalOption(nextValue)) {
      setOverride(nextValue);
      updateLearningGoal(nextValue);
    }
  };

  if (loading) {
    return (
      <Grid container direction="column" aria-hidden="true">
        <Skeleton variant="rect" width={120} height={16} />
        <Skeleton variant="rect" width={240} height={20} />
      </Grid>
    );
  }

  return (
    <SettingRow label={label} labelId={LABEL_ID}>
      <SelectField
        id="learning-goal-select"
        data-testid="learning-goal-select"
        aria-labelledby={LABEL_ID}
        css={styles.select}
        placeholder={_t('Select an option')}
        value={selectedValue ?? ''}
        onChange={handleChange}
        fullWidth
      >
        {LEARNING_GOAL_OPTION_IDS.map((optionId) => (
          <SelectOption key={optionId} value={optionId}>
            {optionNames[optionId]}
          </SelectOption>
        ))}
      </SelectField>
    </SettingRow>
  );
};

export default LearningGoalSettingItem;
